import { type DragEvent } from 'react';
import { GripVertical, Pencil, Trash2, User } from 'lucide-react';
import { Badge } from '../../ui';
import { TASK_STATUSES, priorityMeta } from '../../features/tasks/taskModel';
import type { Task, TaskStatus } from '../../features/tasks/types';

export interface TaskCardProps {
  task: Task;
  /** True while a drag is in progress for this card. */
  dragging?: boolean;
  onDragStart: (e: DragEvent<HTMLElement>, task: Task) => void;
  onDragEnd: () => void;
  onEdit: (task: Task) => void;
  /** Opens the task's detail page (title click). Falls back to onEdit. */
  onOpen?: (task: Task) => void;
  onDelete: (task: Task) => void;
  /** Accessible (non-drag) status move. */
  onMove: (task: Task, status: TaskStatus) => void;
}

/**
 * A single Kanban card. Draggable via native HTML5 DnD (grip handle affordance)
 * with edit/delete actions, plus a keyboard-accessible status select so status
 * can be changed without dragging.
 */
export function TaskCard({
  task,
  dragging,
  onDragStart,
  onDragEnd,
  onEdit,
  onOpen,
  onDelete,
  onMove,
}: TaskCardProps) {
  const prio = priorityMeta(task.priority);

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, task)}
      onDragEnd={onDragEnd}
      className={[
        'group rounded-xl border border-surface-border bg-surface-1 p-3 shadow-sm',
        'transition-colors hover:border-zinc-600',
        dragging ? 'opacity-40' : 'opacity-100',
      ].join(' ')}
    >
      <div className="flex items-start gap-2">
        <GripVertical
          className="mt-0.5 h-4 w-4 shrink-0 cursor-grab text-zinc-600"
          aria-hidden="true"
        />
        <button
          type="button"
          onClick={() => (onOpen ?? onEdit)(task)}
          className="flex-1 text-left text-sm font-medium text-zinc-100 hover:text-white"
        >
          {task.title}
        </button>
        <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
          <button
            type="button"
            onClick={() => onEdit(task)}
            aria-label="Edit task"
            className="rounded-md p-1 text-zinc-500 hover:bg-surface-3 hover:text-zinc-200"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
          <button
            type="button"
            onClick={() => onDelete(task)}
            aria-label="Delete task"
            className="rounded-md p-1 text-zinc-500 hover:bg-surface-3 hover:text-red-300"
          >
            <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        </div>
      </div>

      {task.description && (
        <p className="mt-1.5 line-clamp-2 pl-6 text-xs text-zinc-500">
          {task.description}
        </p>
      )}

      <div className="mt-2.5 flex flex-wrap items-center gap-1.5 pl-6">
        {prio && (
          <Badge tone={prio.tone} dot>
            {prio.label}
          </Badge>
        )}
        {(task.tags ?? []).map((tag) => (
          <Badge key={tag} tone="neutral">
            {tag}
          </Badge>
        ))}
        {task.assignee && (
          <span className="ml-auto inline-flex items-center gap-1 text-xs text-zinc-500">
            <User className="h-3 w-3" aria-hidden="true" />
            {task.assignee}
          </span>
        )}
      </div>

      <div className="mt-2.5 pl-6">
        <label className="sr-only" htmlFor={`move-${task.id}`}>
          Move task to status
        </label>
        <select
          id={`move-${task.id}`}
          value={task.status}
          onChange={(e) => onMove(task, e.target.value as TaskStatus)}
          className="h-7 w-full rounded-lg border border-surface-border bg-surface-2 px-2 text-xs text-zinc-300 transition-colors hover:border-zinc-600 focus:border-accent focus-visible:outline-none"
        >
          {TASK_STATUSES.map((s) => (
            <option key={s.value} value={s.value}>
              Move to: {s.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

export default TaskCard;
